/**
 * 组织创建/编辑表单对话框
 */
import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, InputNumber, TreeSelect } from 'antd';
import { message } from '@/utils/antdStatic';
import {
  createOrganization,
  updateOrganization,
  getOrganizationTree,
} from '../../api/organization';
import type { Organization, OrganizationFormData, OrganizationTreeNode } from '../../api/types';
import { OrganizationType, OrganizationStatus } from '../../api/types';
import type { RelationType } from '@/api/organization';

interface OrganizationFormProps {
  visible: boolean;
  organization: Organization | null;
  parentId: string | undefined;
  onCancel: () => void;
  onSuccess: () => void;
}

/** 组织类型选项 */
const typeOptions = [
  { value: OrganizationType.COMPANY, label: '公司' },
  { value: OrganizationType.DEPARTMENT, label: '部门' },
  { value: OrganizationType.GROUP, label: '分组' },
  { value: OrganizationType.TEAM, label: '团队' },
];

/** 组织状态选项 */
const statusOptions = [
  { value: OrganizationStatus.ACTIVE, label: '启用' },
  { value: OrganizationStatus.INACTIVE, label: '停用' },
];

const ORG_TYPE_TO_RELATION_TYPE: Record<OrganizationType, RelationType> = {
  [OrganizationType.COMPANY]: 'headquarters',
  [OrganizationType.DEPARTMENT]: 'division',
  [OrganizationType.GROUP]: 'region',
  [OrganizationType.TEAM]: 'store',
};

const RELATION_TYPE_TO_ORG_TYPE: Partial<Record<RelationType, OrganizationType>> = {
  headquarters: OrganizationType.COMPANY,
  subsidiary: OrganizationType.COMPANY,
  branch: OrganizationType.COMPANY,
  division: OrganizationType.DEPARTMENT,
  region: OrganizationType.GROUP,
  store: OrganizationType.TEAM,
  direct_store: OrganizationType.TEAM,
};

const getRelationTypes = (source?: any): RelationType[] => {
  if (!source) return [];
  return (source.relationTypes ?? source.relation_types ?? []) as RelationType[];
};

const getOrganizationType = (source?: any): OrganizationType => {
  const explicitType = Number(source?.type);
  if (typeOptions.some(option => option.value === explicitType)) {
    return explicitType as OrganizationType;
  }

  const relationType = getRelationTypes(source)[0];
  return RELATION_TYPE_TO_ORG_TYPE[relationType] ?? OrganizationType.DEPARTMENT;
};

const toOptionalNumber = (value?: string | number | null): number | undefined => {
  if (value === undefined || value === null || value === '' || String(value) === '0') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const buildCreatePayload = (values: any) => {
  const mappedRelationType =
    ORG_TYPE_TO_RELATION_TYPE[Number(values.type) as OrganizationType] ?? 'division';

  return {
    parent_id: toOptionalNumber(values.parentId),
    name: values.name,
    code: values.code,
    relation_types: [mappedRelationType],
    contact_phone: values.phone,
    contact_email: values.email,
    address: values.address,
    remark: values.description,
    sort_order: values.sort ?? 0,
  };
};

const buildUpdatePayload = (values: any) => {
  const { parent_id: _parentId, code: _code, ...payload } = buildCreatePayload(values);
  return {
    ...payload,
    status: values.status === OrganizationStatus.INACTIVE || values.status === 'inactive'
      ? 'inactive'
      : 'active',
  };
};

const OrganizationForm: React.FC<OrganizationFormProps> = ({
  visible,
  organization,
  parentId,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [orgTreeData, setOrgTreeData] = useState<any[]>([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const isEdit = !!organization;

  /** 转换组织树为 TreeSelect 数据 */
  const convertOrgTree = (nodes: OrganizationTreeNode[]): any[] =>
    nodes.map((n) => ({
      value: n.key || n.value,
      title: n.title,
      children: n.children?.length ? convertOrgTree(n.children) : undefined,
    }));

  /** 加载组织树 */
  const fetchOrgTree = async () => {
    try {
      const res: any = await getOrganizationTree();
      const data = res?.data?.data || res?.data;
      const nodes = Array.isArray(data) ? data : data?.list || [];
      setOrgTreeData(convertOrgTree(nodes));
    } catch {
      setOrgTreeData([]);
    }
  };

  useEffect(() => {
    if (visible) fetchOrgTree();
  }, [visible]);

  useEffect(() => {
    if (visible && organization) {
      // 编辑模式：回填表单
      form.setFieldsValue({
        parentId: organization.parent_id || undefined,
        name: organization.name,
        code: organization.code,
        type: getOrganizationType(organization),
        sort: organization.sort ?? (organization as any).sort_order ?? 0,
        description: organization.description ?? (organization as any).remark,
        status: organization.status === 'active'
          ? OrganizationStatus.ACTIVE
          : OrganizationStatus.INACTIVE,
      });
    } else if (visible) {
      // 新建模式：重置表单并设置默认值
      form.resetFields();
      form.setFieldsValue({
        parentId: parentId || undefined,
        type: OrganizationType.DEPARTMENT,
        sort: 0,
        status: OrganizationStatus.ACTIVE,
      });
    }
  }, [visible, organization, parentId, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitLoading(true);

      if (isEdit) {
        await updateOrganization(
          organization!.id,
          buildUpdatePayload(values) as unknown as Partial<OrganizationFormData>,
        );
        message.success('更新成功');
      } else {
        await createOrganization(buildCreatePayload(values) as unknown as OrganizationFormData);
        message.success('创建成功');
      }

      onSuccess();
    } catch (error: any) {
      if (error?.errorFields) return;
      console.error('提交失败', error);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Modal
      title={isEdit ? '编辑组织' : '新建组织'}
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={submitLoading}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          type: OrganizationType.DEPARTMENT,
          sort: 0,
          status: OrganizationStatus.ACTIVE,
        }}
      >
        <Form.Item
          name="parentId"
          label="上级组织"
        >
          <TreeSelect
            placeholder="选择上级组织（留空为顶级）"
            treeData={orgTreeData}
            allowClear
            showSearch
            treeNodeFilterProp="title"
            disabled={isEdit}
          />
        </Form.Item>

        <Form.Item
          name="name"
          label="组织名称"
          rules={[{ required: true, message: '请输入组织名称' }]}
        >
          <Input placeholder="请输入组织名称" maxLength={100} />
        </Form.Item>

        <Form.Item
          name="code"
          label="组织编码"
          rules={[
            { required: true, message: '请输入组织编码' },
            {
              pattern: /^[A-Za-z0-9_-]+$/,
              message: '只能包含字母、数字、下划线和连字符',
            },
          ]}
        >
          <Input
            placeholder="请输入组织编码"
            maxLength={50}
            disabled={isEdit}
          />
        </Form.Item>

        <Form.Item
          name="type"
          label="组织类型"
          rules={[{ required: true, message: '请选择组织类型' }]}
        >
          <Select placeholder="请选择组织类型" options={typeOptions} />
        </Form.Item>

        <Form.Item
          name="sort"
          label="排序"
        >
          <InputNumber min={0} max={9999} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="description"
          label="描述"
        >
          <Input.TextArea
            placeholder="请输入描述"
            rows={3}
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Form.Item
          name="status"
          label="状态"
          rules={[{ required: true, message: '请选择状态' }]}
        >
          <Select placeholder="请选择状态" options={statusOptions} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default OrganizationForm;
